import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward,
  Bookmark,
  BookmarkCheck,
  Settings,
  X,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useRSVP, splitWordByORP } from '../hooks/useRSVP';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WPM_PRESETS = [150, 250, 350, 500, 700, 900];

export default function Reader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [document, setDocument] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [bookmarks, setBookmarks] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const [fontSize, setFontSize] = useState(user?.settings?.font_size || 36);
  const [showORP, setShowORP] = useState(user?.settings?.show_orp !== false);
  const [pauseOnPunctuation, setPauseOnPunctuation] = useState(user?.settings?.pause_on_punctuation !== false);
  const [skipArticles, setSkipArticles] = useState(user?.settings?.skip_articles || false);
  
  const controlsTimeoutRef = useRef(null);
  const sessionStartRef = useRef(null);
  const startPositionRef = useRef(0);
  
  const {
    currentWord,
    currentIndex,
    isPlaying,
    wpm,
    setWpm,
    progress,
    wordsRemaining,
    estimatedTimeRemaining,
    totalWords,
    play,
    pause,
    toggle,
    goToPosition,
    skipForward,
    skipBackward,
    setCurrentIndex
  } = useRSVP(words, document?.current_position || 0, {
    wpm: user?.settings?.wpm || 300,
    pauseOnPunctuation,
    skipArticles
  });

  // Fetch document content
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axios.get(`${API}/documents/${id}/content`);
        setDocument(response.data);
        setWords(response.data.words);
        setBookmarks(response.data.bookmarks || []);
        setCurrentIndex(response.data.current_position || 0);
      } catch (error) {
        toast.error('Failed to load document');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocument();
  }, [id, navigate, setCurrentIndex]);

  // Track reading session
  useEffect(() => {
    if (isPlaying && !sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      startPositionRef.current = currentIndex;
    }
  }, [isPlaying, currentIndex]);

  // Save progress periodically
  useEffect(() => {
    if (!document || words.length === 0) return;
    
    const saveProgress = async () => {
      try {
        await axios.put(`${API}/documents/${id}/progress`, {
          current_position: currentIndex,
          progress_percent: progress
        });
      } catch (error) {
        console.error('Failed to save progress');
      }
    };

    const interval = setInterval(saveProgress, 10000); // Every 10 seconds
    return () => {
      clearInterval(interval);
      saveProgress(); // Save on unmount
    };
  }, [id, currentIndex, progress, document, words.length]);

  // Record session on pause or unmount
  const recordSession = useCallback(async () => {
    if (!sessionStartRef.current) return;
    
    const duration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
    const wordsRead = currentIndex - startPositionRef.current;
    
    if (duration > 5 && wordsRead > 0) {
      try {
        await axios.post(`${API}/statistics/session`, {
          document_id: id,
          words_read: wordsRead,
          duration_seconds: duration,
          average_wpm: Math.round((wordsRead / duration) * 60)
        });
      } catch (error) {
        console.error('Failed to record session');
      }
    }
    
    sessionStartRef.current = null;
  }, [id, currentIndex]);

  useEffect(() => {
    if (!isPlaying && sessionStartRef.current) {
      recordSession();
    }
  }, [isPlaying, recordSession]);

  useEffect(() => {
    return () => {
      recordSession();
    };
  }, [recordSession]);

  // Auto-hide controls
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [isPlaying]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          toggle();
          showControlsTemporarily();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipBackward(e.shiftKey ? 50 : 10);
          showControlsTemporarily();
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipForward(e.shiftKey ? 50 : 10);
          showControlsTemporarily();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setWpm(prev => Math.min(prev + 50, 1000));
          showControlsTemporarily();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setWpm(prev => Math.max(prev - 50, 100));
          showControlsTemporarily();
          break;
        case 'KeyB':
          e.preventDefault();
          toggleBookmark();
          break;
        case 'Escape':
          navigate('/dashboard');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle, skipForward, skipBackward, setWpm, showControlsTemporarily, navigate]);

  // Touch/Click handler
  const handleScreenTap = (e) => {
    // Ignore if tapping on controls
    if (e.target.closest('[data-controls]')) return;
    
    showControlsTemporarily();
    
    // Double tap to toggle play
    if (e.detail === 2) {
      toggle();
    }
  };

  // Bookmark functions
  const toggleBookmark = async () => {
    const isBookmarked = bookmarks.includes(currentIndex);
    
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/documents/${id}/bookmarks/${currentIndex}`);
        setBookmarks(prev => prev.filter(b => b !== currentIndex));
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/documents/${id}/bookmarks`, { position: currentIndex });
        setBookmarks(prev => [...prev, currentIndex].sort((a, b) => a - b));
        toast.success('Bookmark added');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const goToNextBookmark = () => {
    const next = bookmarks.find(b => b > currentIndex);
    if (next !== undefined) {
      goToPosition(next);
      toast.success(`Jumped to bookmark at ${Math.round((next / totalWords) * 100)}%`);
    }
  };

  const goToPrevBookmark = () => {
    const prev = [...bookmarks].reverse().find(b => b < currentIndex);
    if (prev !== undefined) {
      goToPosition(prev);
      toast.success(`Jumped to bookmark at ${Math.round((prev / totalWords) * 100)}%`);
    }
  };

  const wordParts = splitWordByORP(currentWord);
  const isCurrentBookmarked = bookmarks.includes(currentIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background flex flex-col select-none"
      onClick={handleScreenTap}
      onMouseMove={showControlsTemporarily}
    >
      {/* Top Bar */}
      <AnimatePresence>
        {showControls && (
          <motion.header
            data-controls
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3"
          >
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Exit
              </Button>
              
              <h1 className="font-medium truncate max-w-[200px] sm:max-w-md text-sm" data-testid="document-title">
                {document?.title}
              </h1>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  data-testid="reader-theme-toggle"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
                
                <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="reader-settings-btn">
                      <Settings className="w-4 h-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent data-controls>
                    <SheetHeader>
                      <SheetTitle>Reading Settings</SheetTitle>
                      <SheetDescription>
                        Customize your reading experience
                      </SheetDescription>
                    </SheetHeader>
                    
                    <div className="mt-6 space-y-6">
                      {/* Font Size */}
                      <div className="space-y-3">
                        <Label>Font Size: {fontSize}px</Label>
                        <div className="flex items-center gap-4">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setFontSize(Math.max(24, fontSize - 4))}
                            data-testid="font-size-decrease"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Slider
                            value={[fontSize]}
                            onValueChange={([v]) => setFontSize(v)}
                            min={24}
                            max={72}
                            step={4}
                            className="flex-1"
                            data-testid="font-size-slider"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setFontSize(Math.min(72, fontSize + 4))}
                            data-testid="font-size-increase"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* ORP Highlighting */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label>ORP Highlighting</Label>
                          <p className="text-sm text-muted-foreground">
                            Highlight the focus point of each word
                          </p>
                        </div>
                        <Switch
                          checked={showORP}
                          onCheckedChange={setShowORP}
                          data-testid="orp-toggle"
                        />
                      </div>
                      
                      {/* Punctuation Pause */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label>Punctuation Pauses</Label>
                          <p className="text-sm text-muted-foreground">
                            Pause longer at sentence endings
                          </p>
                        </div>
                        <Switch
                          checked={pauseOnPunctuation}
                          onCheckedChange={setPauseOnPunctuation}
                          data-testid="punctuation-toggle"
                        />
                      </div>
                      
                      {/* Skip Articles */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label>Quick Articles</Label>
                          <p className="text-sm text-muted-foreground">
                            Show "a", "an", "the" briefly
                          </p>
                        </div>
                        <Switch
                          checked={skipArticles}
                          onCheckedChange={setSkipArticles}
                          data-testid="articles-toggle"
                        />
                      </div>
                      
                      {/* Bookmarks */}
                      {bookmarks.length > 0 && (
                        <div className="space-y-3">
                          <Label>Bookmarks ({bookmarks.length})</Label>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={goToPrevBookmark}
                              disabled={!bookmarks.some(b => b < currentIndex)}
                              data-testid="prev-bookmark-btn"
                            >
                              <ChevronLeft className="w-4 h-4 mr-1" />
                              Previous
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={goToNextBookmark}
                              disabled={!bookmarks.some(b => b > currentIndex)}
                              data-testid="next-bookmark-btn"
                            >
                              Next
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Keyboard Shortcuts */}
                      <div className="space-y-3">
                        <Label>Keyboard Shortcuts</Label>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><kbd className="px-1.5 py-0.5 bg-muted rounded">Space</kbd> Play/Pause</p>
                          <p><kbd className="px-1.5 py-0.5 bg-muted rounded">← →</kbd> Skip words</p>
                          <p><kbd className="px-1.5 py-0.5 bg-muted rounded">↑ ↓</kbd> Adjust speed</p>
                          <p><kbd className="px-1.5 py-0.5 bg-muted rounded">B</kbd> Add bookmark</p>
                          <p><kbd className="px-1.5 py-0.5 bg-muted rounded">Esc</kbd> Exit reader</p>
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* RSVP Display */}
      <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div className="w-full text-center" data-testid="rsvp-display">
          {/* Focus guides */}
          <div className="relative inline-flex flex-col items-center w-full">
            <div className="w-px h-4 bg-primary/30 focus-guide-line" />
            
            <div className="py-8 w-full flex justify-center items-center min-h-[120px]">
              <span 
                className="font-reader tracking-wide whitespace-nowrap"
                style={{ 
                  fontSize: `${fontSize}px`,
                  maxWidth: '95vw',
                  display: 'inline-block'
                }}
                data-testid="current-word"
              >
                {showORP ? (
                  <>
                    <span className="text-foreground">{wordParts.before}</span>
                    <span className="orp-highlight">{wordParts.orp}</span>
                    <span className="text-foreground">{wordParts.after}</span>
                  </>
                ) : (
                  <span className="text-foreground">{currentWord}</span>
                )}
              </span>
            </div>
            
            <div className="w-px h-4 bg-primary/30 focus-guide-line" />
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            data-controls
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-t border-border px-4 py-4"
          >
            <div className="max-w-2xl mx-auto space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <Progress value={progress} className="h-2 cursor-pointer" data-testid="progress-bar" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span data-testid="progress-percent">{Math.round(progress)}% complete</span>
                  <span data-testid="time-remaining">~{estimatedTimeRemaining} min remaining</span>
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => skipBackward(50)}
                  data-testid="skip-back-50"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => skipBackward(10)}
                  data-testid="skip-back-10"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                
                <Button
                  size="lg"
                  onClick={toggle}
                  className="btn-pill px-8 h-12"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-5 h-5 mr-2" /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" /> Play
                    </>
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => skipForward(10)}
                  data-testid="skip-forward-10"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => skipForward(50)}
                  data-testid="skip-forward-50"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
                
                <Button
                  variant={isCurrentBookmarked ? 'default' : 'ghost'}
                  size="icon"
                  onClick={toggleBookmark}
                  data-testid="bookmark-btn"
                >
                  {isCurrentBookmarked ? (
                    <BookmarkCheck className="w-5 h-5" />
                  ) : (
                    <Bookmark className="w-5 h-5" />
                  )}
                </Button>
              </div>

              {/* Speed Control */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" data-testid="wpm-display">{wpm} WPM</span>
                  <div className="flex gap-1">
                    {WPM_PRESETS.map(preset => (
                      <button
                        key={preset}
                        onClick={() => setWpm(preset)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          wpm === preset 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                        data-testid={`wpm-preset-${preset}`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
                <Slider
                  value={[wpm]}
                  onValueChange={([v]) => setWpm(v)}
                  min={100}
                  max={1000}
                  step={10}
                  data-testid="wpm-slider"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
